<!--
  @component Navbar.LinkWrapper

  A wrapper component for navbar links that handles click and keydown events.
  It provides a way to create custom navbar items while maintaining consistent behavior with the navbar state.

  @prop href - The URL the link points to (required)
  @prop label - The accessible label for the link (required)
  @prop css - Custom CSS styles to apply to the link wrapper

  Example:
  <Navbar.LinkWrapper href="/product/cool-sneakers" label="Cool Sneakers">
    <ProductCard title="Cool Sneakers" price="$129.99" />
  </Navbar.LinkWrapper>
-->

<script lang="ts">
  import { getContext } from 'svelte';
  import { css } from '@agapi/ui/panda/css';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarLinkWrapperProps } from '../navbar.types';

  /** Props for the NavbarLinkWrapper component */
  const { href, label, css: cssProp, children }: NavbarLinkWrapperProps = $props();

  /** Navbar store for managing navbar state */
  const navbarStore = getContext<NavbarStore>('navbar');

  /** Unique identifier for the link wrapper */
  const id = $props.id();

  /**
   * Handles click event on the link
   * Closes any open items and menus
   */
  const onclick = () => {
    navbarStore.setOpenItem(null);
    navbarStore.setOpenMenuItem(null);
    navbarStore.closeMenu();
  }

  /**
   * Handles keydown event on the link
   * Closes the menu if Escape key is pressed
   * @param {KeyboardEvent} e - The keydown event
   */
  const onkeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      navbarStore.closeMenu();
    }
  }
</script>

<a
  {onclick}
  {onkeydown}
  href={href}
  aria-label={label}
  data-id={id}
  data-type="link-wrapper"
  class={css(cssProp)}
>
  {@render children?.()}
</a>
